import { Separator } from '@qr-front/ui/components/Separator'
import {
	BottomDialog,
	BottomDialogContent,
	BottomDialogTitle,
} from '@qr-front/ui/dashboard/components/BottomDialog'
import { subExpiredDialogAtom } from 'features/library/client'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useTranslations } from 'next-intl'
import { UserStatusEnum } from 'types/subscription'

import { userAtom } from '@/global-state'
import { useRouter } from '@/i18n-routing'
import { getTrialRemainingTime } from '@/utils'

import {
	mobileTableRowActionDialogAtom,
	mobileTableRowActionDialogTypeAtom,
	qrItemState,
	shareQrDialogAtom,
	shareQrLinkAtom,
} from '../dialogsState'
import {
	downloadActions,
	moreActionsMobile,
	TABLE_ACTION_TYPE,
	TABLE_ROW_ACTION_TYPE,
	TAction,
} from '../TableActions/Actions/actionOptions'
import { ActionOptionsContainer } from '../TableActions/Actions/ActionOptionsContainer'
import { withExpiredDialogIfNotAllowed } from '../TableActions/Actions/tableActionUtils'

export const MobileActionsDialog = () => {
	const t = useTranslations()
	const router = useRouter()

	const qrItem = useAtomValue(qrItemState)
	const [isOpen, setIsOpen] = useAtom(mobileTableRowActionDialogAtom)
	const [dialogType, setDialogType] = useAtom(
		mobileTableRowActionDialogTypeAtom,
	)

	const setOpenExpiredTrialDialog = useSetAtom(subExpiredDialogAtom)

	const setShareQrOpen = useSetAtom(shareQrDialogAtom)
	const setShareQrLink = useSetAtom(shareQrLinkAtom)

	const user = useAtomValue(userAtom)
	const userTrial = getTrialRemainingTime(
		user?.createdAt,
		user?.flowData?.freeDaysQuota,
	)

	if (!qrItem || !isOpen) {
		return null
	}

	const isPremiumCancelled = user?.status === UserStatusEnum.PREMIUM_CANCELLED

	const showExpiredDialog =
		(userTrial.expired &&
			[
				UserStatusEnum.FREEMIUM_OLD,
				UserStatusEnum.PREMIUM_CANCELLED,
				UserStatusEnum.PREMIUM_IN_DEBT,
				UserStatusEnum.DEACTIVATED,
			].includes(user?.status as UserStatusEnum)) ||
		isPremiumCancelled

	const handleDownloadAction = () => {
		withExpiredDialogIfNotAllowed(() =>
			setDialogType(TABLE_ROW_ACTION_TYPE.Download),
		)()
	}

	const handleShareAction = () => {
		setShareQrOpen(true)
		setShareQrLink(qrItem.uri)
		setIsOpen(false)
	}

	const handleOtherAction = (action: TAction) => {
		action(qrItem)
		setIsOpen(false)
	}

	const handleOptionAction = (key: TABLE_ACTION_TYPE, action: TAction) => {
		if (
			showExpiredDialog &&
			!isPremiumCancelled &&
			key !== TABLE_ACTION_TYPE.Download &&
			key !== TABLE_ACTION_TYPE.DownloadAsPng &&
			key !== TABLE_ACTION_TYPE.DownloadAsJpg &&
			key !== TABLE_ACTION_TYPE.DownloadAsSvg &&
			key !== TABLE_ACTION_TYPE.Share &&
			key !== TABLE_ACTION_TYPE.Delete
		) {
			setOpenExpiredTrialDialog(true)
			setIsOpen(false)
			return
		}
		if (key === TABLE_ACTION_TYPE.Download) {
			handleDownloadAction()
			return
		}
		if (key === TABLE_ACTION_TYPE.Share) {
			handleShareAction()
			return
		}
		handleOtherAction(action)
	}

	const dialogTitleKey =
		dialogType === TABLE_ROW_ACTION_TYPE.More
			? 'dashboard__qr_table__qr_card__actions'
			: 'dashboard__qr_table__qr_card__download'

	const dialogActions =
		dialogType === TABLE_ROW_ACTION_TYPE.More
			? moreActionsMobile({ router, qrItem })
			: downloadActions

	return (
		<>
			<BottomDialog open={isOpen} onOpenChange={setIsOpen}>
				<BottomDialogContent>
					<div className="px-5 py-4 pb-4">
						<BottomDialogTitle className="type-heading-4">
							{t(dialogTitleKey)}
						</BottomDialogTitle>
					</div>
					<Separator collapse={false} className="w-full" />
					<div className="px-5 py-4">
						<ActionOptionsContainer
							options={dialogActions}
							handleOptionAction={handleOptionAction}
						/>
					</div>
				</BottomDialogContent>
			</BottomDialog>
		</>
	)
}
